import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import documentApi from '../../services/documentApi';
import { useProjectSelection } from '../ProjectSelection';
import './AddProjectModal.css';
import { ReactComponent as ChevronRightIcon } from '../../svg/tree-chevron-right.svg';
import { ReactComponent as ChevronDownIcon } from '../../svg/tree-chevron-down.svg';
import { ReactComponent as FolderIcon } from '../../svg/tree-folder.svg';
import { ReactComponent as DocumentIcon } from '../../svg/tree-document.svg';
import { ReactComponent as CheckIcon } from '../../svg/tree-check.svg';
import { ReactComponent as ClockIcon } from '../../svg/tree-clock.svg';
import { ReactComponent as DeleteIcon } from '../../svg/tree-delete.svg';

/**
 * AddProjectModal - A modal component for adding new projects or editing existing ones
 * Optimized for performance with memoization and efficient state management
 */

/** Feather-style "tag" glyph used for custom field rows (matches the app's tree icon set). */
const FieldIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width="14"
    height="14"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
    <line x1="7" y1="7" x2="7.01" y2="7" />
  </svg>
);

const AddProjectModal = React.memo(({
  isOpen,
  onClose,
  onAdd,
  onDelete,
  organisation,
  authUser,
  editMode = false,
  initialProjectName = '',
  firebase,
}) => {
  const { t } = useTranslation();
  // State management
  const [selectedOrganisation, setSelectedOrganisation] = useState(organisation || '');
  const [newProjectName, setNewProjectName] = useState('');
  const [customFields, setCustomFields] = useState([]);
  const [projectFolders, setProjectFolders] = useState([]);
  const [originalProjectName, setOriginalProjectName] = useState('');
  const [originalFields, setOriginalFields] = useState([]);
  const [originalProjectFolders, setOriginalProjectFolders] = useState([]);
  const [isCreating, setIsCreating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [savingDefaults, setSavingDefaults] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [error, setError] = useState('');
  const [isLoadingProjectData, setIsLoadingProjectData] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [expandedFolders, setExpandedFolders] = useState({});

  // Derive user's organisations from authUser.roles
  const userOrganisations = useMemo(() => {
    if (!authUser?.roles) return [];
    return Object.keys(authUser.roles).filter((k) => authUser.roles[k] !== null);
  }, [authUser]);

  // Shared project selection (empty string when rendered outside the provider)
  const { selectedProject } = useProjectSelection();

  // Tracks whether the user manually picked an org so the async resolution
  // never overrides an explicit choice made while the lookup is in flight.
  const userPickedOrgRef = useRef(false);

  // Sync selectedOrganisation with organisation prop on open; when the prop is
  // empty, resolve the org from the shared selected project, defaulting to the
  // first user org while resolving and if no match is found.
  useEffect(() => {
    if (!isOpen) return;
    if (organisation) {
      userPickedOrgRef.current = false;
      setSelectedOrganisation(organisation);
      return;
    }
    if (userOrganisations.length === 0) return;

    // Provisional default so the selector is never empty while resolving
    setSelectedOrganisation(userOrganisations[0]);

    if (!selectedProject || !firebase) return;

    let cancelled = false;
    const resolveOrgFromProject = async () => {
      for (const org of userOrganisations) {
        if (cancelled || userPickedOrgRef.current) return;
        try {
          const projectsList = await firebase?.doGetOrganizationProjects?.(org);
          if (Array.isArray(projectsList)) {
            const match = projectsList.find(
              (p) => p.id === selectedProject || p.name === selectedProject || p.title === selectedProject,
            );
            if (match) {
              if (!cancelled && !userPickedOrgRef.current) setSelectedOrganisation(org);
              return;
            }
          }
        } catch (_err) {
          // ignore and continue to the next org
        }
      }
    };

    resolveOrgFromProject();
    return () => { cancelled = true; };
  }, [isOpen, organisation, userOrganisations, selectedProject, firebase]);

  // System fields that cannot be edited as custom fields
  const SYSTEM_FIELDS = ['name', 'createdAt', 'updatedAt', 'createdBy', 'updatedBy', 'projectFolders'];

  // Load project data when modal opens in edit mode
  useEffect(() => {
    let cancelled = false;

    const initializeModal = async () => {
      if (!isOpen) return;

      if (!editMode) {
        setNewProjectName('');
        setOriginalProjectName('');
        setError('');
        setCustomFields([{ key: '', value: '' }]);
        setOriginalFields([]);
        setProjectFolders([]);
        setOriginalProjectFolders([]);
        setDocuments([]);
        setIsLoadingProjectData(false);

        if (selectedOrganisation && firebase) {
          try {
            const configDoc = await firebase.fsdb
              .collection(selectedOrganisation)
              .doc('_orgConfig')
              .get();
            if (!cancelled && configDoc.exists) {
              const config = configDoc.data().defaultProjectConfig;
              if (config) {
                if (config.folders && config.folders.length > 0) {
                  setProjectFolders(config.folders);
                }
                if (config.fields && config.fields.length > 0) {
                  setCustomFields(config.fields);
                }
              }
            }
          } catch (_cfgErr) {
          }
        }
        return;
      }

      setNewProjectName(initialProjectName);
      setOriginalProjectName(initialProjectName);
      setError('');

      if (!initialProjectName || !firebase || !selectedOrganisation) {
        setCustomFields([{ key: '', value: '' }]);
        setOriginalFields([]);
        setProjectFolders([]);
        setOriginalProjectFolders([]);
        return;
      }

      setIsLoadingProjectData(true);
      try {
        const projectDoc = await firebase.fsdb
          .collection(selectedOrganisation)
          .doc(initialProjectName)
          .get();

        if (cancelled) return;

        if (projectDoc.exists) {
          const data = projectDoc.data();

          // Extract custom fields (excluding system fields)
          const fields = Object.entries(data)
            .filter(([key]) => !SYSTEM_FIELDS.includes(key))
            .map(([key, value]) => ({
              key,
              value: typeof value === 'object' ? JSON.stringify(value) : String(value)
            }));

          if (!cancelled) {
            setCustomFields(fields.length > 0 ? fields : [{ key: '', value: '' }]);
            setOriginalFields(JSON.parse(JSON.stringify(fields)));

            const folders = data.projectFolders || [];
            setProjectFolders(folders);
            setOriginalProjectFolders(JSON.parse(JSON.stringify(folders)));
          }
        } else {
          if (!cancelled) {
            setCustomFields([{ key: '', value: '' }]);
            setProjectFolders([]);
            setError(t('addProjectModal.error_project_not_found'));
          }
        }

        if (!cancelled) {
          try {
            const orgDocs = await documentApi.getOrganizationDocuments(
              userOrganisations,
            );
            if (!cancelled) {
              let projectDocs = orgDocs.filter(
                (doc) => doc.projectName === initialProjectName
              );
              if (projectDocs.some((doc) => !doc.folderPath)) {
                projectDocs = await documentApi.enrichWithFolderPaths(
                  projectDocs,
                  [initialProjectName],
                );
              }
              setDocuments(projectDocs);
            }
          } catch (_docErr) {
            if (!cancelled) setDocuments([]);
          }
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Error loading project data:', err);
          setCustomFields([{ key: '', value: '' }]);
          setProjectFolders([]);
          setError(t('addProjectModal.error_loading_data'));
        }
      } finally {
        if (!cancelled) {
          setIsLoadingProjectData(false);
        }
      }
    };

    initializeModal();

    return () => {
      cancelled = true;
    };
  }, [isOpen, editMode, initialProjectName, firebase, selectedOrganisation]);

  // Handlers with useCallback for optimization
  const handleAddField = useCallback(() => {
    setCustomFields(prev => [...prev, { key: '', value: '' }]);
  }, []);

  const handleRemoveField = useCallback((index) => {
    setCustomFields(prev => {
      const newFields = prev.filter((_, i) => i !== index);
      return newFields.length > 0 ? newFields : [{ key: '', value: '' }];
    });
  }, []);

  const handleFieldChange = useCallback((index, field, value) => {
    setCustomFields((prev) =>
      prev.map((f, i) => (i === index ? { ...f, [field]: value } : f)),
    );
  }, []);

  const handleAddFolder = useCallback(() => {
    setProjectFolders(prev => [...prev, '']);
  }, []);

  const handleRemoveFolder = useCallback((index) => {
    setProjectFolders(prev => {
      const newFolders = prev.filter((_, i) => i !== index);
      // Allow removing all folders - user must add at least one before saving
      return newFolders;
    });
  }, []);

  const handleFolderChange = useCallback((index, value) => {
    setProjectFolders(prev => {
      const newFolders = [...prev];
      newFolders[index] = value;
      return newFolders;
    });
  }, []);

  // Check if there are changes to enable/disable update button
  const hasChanges = useCallback(() => {
    // Check if project name changed
    if (newProjectName.trim() !== originalProjectName) {
      return true;
    }

    // Check if project folders changed
    if (JSON.stringify(projectFolders) !== JSON.stringify(originalProjectFolders)) {
      return true;
    }

    const currentFields = customFields.filter(
      (f) => f.key.trim(),
    );
    const origFields = originalFields.filter(
      (f) => f.key.trim(),
    );
    if (JSON.stringify(currentFields) !== JSON.stringify(origFields)) {
      return true;
    }

    return false;
  }, [newProjectName, originalProjectName, projectFolders, originalProjectFolders, customFields, originalFields]);

  const handleSubmit = useCallback(async () => {
    if (!newProjectName.trim()) {
      setError(t('addProjectModal.error_name_required'));
      return;
    }

    // Validate that at least one folder is provided
    const cleanedFolders = projectFolders.filter(f => f.trim());
    if (cleanedFolders.length === 0) {
      setError(t('addProjectModal.error_folder_required'));
      return;
    }

    // Validate custom fields
    const validFields = customFields.filter(f => f.key.trim());
    const fieldKeys = validFields.map(f => f.key.trim());
    const duplicateKeys = fieldKeys.filter((key, index) => fieldKeys.indexOf(key) !== index);

    if (duplicateKeys.length > 0) {
      setError(t('addProjectModal.error_duplicate_fields', { keys: duplicateKeys.join(', ') }));
      return;
    }

    setIsCreating(true);
    setError('');

    try {
      const projectData = validFields.reduce((acc, field) => {
        acc[field.key.trim()] = field.value.trim();
        return acc;
      }, {});

      // Identify fields to delete (fields that were in original but not in current)
      const fieldsToDelete = [];
      if (editMode && originalFields.length > 0) {
        const currentFieldKeys = validFields.map(f => f.key.trim());
        originalFields.forEach(originalField => {
          if (originalField.key.trim() && !currentFieldKeys.includes(originalField.key.trim())) {
            fieldsToDelete.push(originalField.key.trim());
          }
        });
      }

      await onAdd(
        newProjectName.trim(),
        authUser,
        editMode,
        initialProjectName,
        projectData,
        fieldsToDelete,
        cleanedFolders,
        selectedOrganisation
      );

      // Reset form
      setNewProjectName('');
      setCustomFields([{ key: '', value: '' }]);
      setProjectFolders([]);
      setOriginalFields([]);
      setOriginalProjectFolders([]);
      setOriginalProjectName('');
      onClose();
    } catch (err) {
      setError(err.message || t('vcontrol.error_failed_to', { action: editMode ? t('vcontrol.actions.update') : t('vcontrol.actions.create') }));
    } finally {
      setIsCreating(false);
    }
  }, [
    newProjectName,
    customFields,
    projectFolders,
    editMode,
    originalFields,
    initialProjectName,
    authUser,
    onAdd,
    onClose,
    selectedOrganisation,
    t
  ]);

  const handleSaveDefaults = useCallback(async () => {
    if (!firebase || !selectedOrganisation) return;
    setSavingDefaults(true);
    try {
      const validFields = customFields.filter((f) => f.key.trim());
      await firebase.fsdb
        .collection(selectedOrganisation)
        .doc('_orgConfig')
        .set(
          {
            defaultProjectConfig: {
              folders: projectFolders.filter((f) => f.trim()),
              fields: validFields,
            },
          },
          { merge: true }
        );
      setError('');
      setSavingDefaults(false);
      return true;
    } catch (err) {
      console.error('Error saving default config:', err);
      setError(
        err.message || t('addProjectModal.error_saving_defaults')
      );
      setSavingDefaults(false);
      return false;
    }
  }, [
    firebase,
    selectedOrganisation,
    projectFolders,
    customFields,
    t,
  ]);

  const handleClose = useCallback(() => {
    setNewProjectName('');
    setCustomFields([{ key: '', value: '' }]);
    setProjectFolders([]);
    setOriginalFields([]);
    setOriginalProjectFolders([]);
    setOriginalProjectName('');
    setShowDeleteConfirm(false);
    setError('');
    setDocuments([]);
    setExpandedFolders({});
    onClose();
  }, [onClose]);

  // Handle project deletion with confirmation
  const handleDelete = useCallback(async () => {
    if (!showDeleteConfirm) {
      setShowDeleteConfirm(true);
      return;
    }

    if (!initialProjectName) {
      setError(t('vcontrol.error_no_project_delete'));
      return;
    }

    setIsDeleting(true);
    setError('');

    try {
      await firebase.doDeleteProject(selectedOrganisation, initialProjectName, authUser?.uid, authUser?.email);
      if (onDelete) {
        await onDelete(initialProjectName);
      }
      setShowDeleteConfirm(false);
      handleClose();
    } catch (err) {
      console.error('Error deleting project:', err);
      setError(err.message || t('vcontrol.error_delete_failed', { error: '' }));
      setShowDeleteConfirm(false);
    } finally {
      setIsDeleting(false);
    }
  }, [showDeleteConfirm, initialProjectName, firebase, selectedOrganisation, onDelete, handleClose]);

  const getFolderDepth = (folder) => {
    if (!folder || typeof folder !== 'string') return 0;
    const segments = folder.split('/').filter(Boolean);
    return Math.max(0, segments.length - 1);
  };

  const docCountByFolder = useMemo(() => {
    const counts = {};
    documents.forEach((doc) => {
      const raw = (doc.folderPath || '').replace(/^\/+|\/+$/g, '').toLowerCase();
      const folderPath = raw || 'default';
      counts[folderPath] = (counts[folderPath] || 0) + 1;
    });
    return counts;
  }, [documents]);

  const docsByFolder = useMemo(() => {
    const grouped = {};
    documents.forEach((doc) => {
      const raw = (doc.folderPath || '').replace(/^\/+|\/+$/g, '').toLowerCase();
      const folderPath = raw || 'default';
      if (!grouped[folderPath]) grouped[folderPath] = [];
      grouped[folderPath].push(doc);
    });
    return grouped;
  }, [documents]);

  const normalizeFolder = (folder) => {
    if (!folder) return 'default';
    return folder.replace(/^\/+|\/+$/g, '').toLowerCase();
  };

  const isAdmin = useMemo(
    () =>
      authUser?.isAdmin === true ||
      Object.keys(authUser?.roles || {}).some(
        (k) => k === 'ADMIN' || k === 'admin',
      ) ||
      Object.values(authUser?.roles || {}).some(
        (v) => typeof v === 'string' && v.toUpperCase() === 'ADMIN',
      ),
    [authUser],
  );

  if (!isOpen) return null;

  return createPortal(
    <div className="add-project-backdrop" onClick={handleClose}>
      <div className="add-project-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          <h2>{editMode ? t('addProjectModal.title_edit') : t('addProjectModal.title_add')}</h2>
          <button
            className="close-button"
            onClick={handleClose}
            title={t('addProjectModal.cancel')}
            disabled={isCreating || isDeleting}
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>

        {/* Organisation selector / indicator */}
        {userOrganisations.length > 0 && (
          <div className={`add-project-org-indicator ${editMode ? 'add-project-org-indicator--readonly' : ''}`}>
            <label className="add-project-org-indicator__label" htmlFor="addProjectOrgSelect">
              {t('addProjectModal.organisation_label')}
            </label>
            {editMode ? (
              <span className="add-project-org-indicator__value">
                {selectedOrganisation}
              </span>
            ) : (
              <select
                id="addProjectOrgSelect"
                className="add-project-org-indicator__select"
                value={selectedOrganisation}
                onChange={(e) => {
                  userPickedOrgRef.current = true;
                  setSelectedOrganisation(e.target.value);
                }}
                disabled={isCreating || isDeleting}
              >
                {userOrganisations.map((org) => (
                  <option key={org} value={org}>
                    {org}
                  </option>
                ))}
              </select>
            )}
          </div>
        )}

        {/* Body */}
        <div className="modal-body">
          {/* Project Name Section */}
          <div className="form-group">
            <label htmlFor="newProjectName">
              {t('addProjectModal.project_name_required')}
            </label>
            <input
              type="text"
              id="newProjectName"
              value={newProjectName}
              onChange={(e) => setNewProjectName(e.target.value)}
              placeholder={t('addProjectModal.enter_project_name')}
              disabled={isCreating || isLoadingProjectData}
              autoFocus
            />
          </div>

          {/* Project Folders Section */}
          <div className="add-project-section add-project-section--folders">
            <div className="add-project-section-header">
              <label htmlFor="projectFolders">{t('addProjectModal.project_folders')}</label>
              {!isLoadingProjectData && (
                <button
                  type="button"
                  className="add-project-add-btn add-project-add-btn--minimal"
                  onClick={handleAddFolder}
                  disabled={isCreating}
                  title={t('addProjectModal.add_folder')}
                >
                  <span aria-hidden="true">+</span> {t('addProjectModal.add_folder')}
                </button>
              )}
            </div>
            {isLoadingProjectData ? (
              <div className="add-project-empty add-project-empty--tree">
                {t('addProjectModal.loading_folders')}
              </div>
            ) : projectFolders.length === 0 ? (
              <div className="add-project-empty add-project-empty--tree">
                {t('addProjectModal.no_folders')}
              </div>
            ) : (
              <ul className="add-project-tree">
                {projectFolders.map((folder, index) => {
                  const normFolder = normalizeFolder(folder);
                  const folderDocs = docsByFolder[normFolder] || [];
                  const hasDocs = folderDocs.length > 0;
                  const isExpanded = expandedFolders[normFolder];
                  const folderDepth = Math.min(getFolderDepth(folder), 5);

                  return (
                    <li key={index} data-folder-depth={folderDepth}>
                      <div
                        className={`add-project-tree-node ${hasDocs ? 'add-project-tree-node--expandable' : ''} ${isExpanded ? 'add-project-tree-node--open' : ''}`}
                        data-depth={folderDepth}
                      >
                        {hasDocs ? (
                          <button
                            type="button"
                            className={`add-project-tree-toggle ${isExpanded ? 'add-project-tree-toggle--open' : ''}`}
                            onClick={() =>
                              setExpandedFolders((prev) => ({
                                ...prev,
                                [normFolder]: !prev[normFolder],
                              }))
                            }
                            aria-expanded={isExpanded}
                            aria-label={
                              isExpanded
                                ? t('addProjectModal.collapse_folder')
                                : t('addProjectModal.expand_folder')
                            }
                            disabled={isCreating}
                          >
                            {isExpanded ? <ChevronDownIcon /> : <ChevronRightIcon />}
                          </button>
                        ) : (
                          <span
                            className="add-project-tree-toggle add-project-tree-toggle--spacer"
                            aria-hidden="true"
                          />
                        )}
                        <span className="add-project-tree-icon" aria-hidden="true">
                          <FolderIcon />
                        </span>
                        <input
                          type="text"
                          className="add-project-tree-input"
                          placeholder={t('addProjectModal.folder_placeholder')}
                          value={folder}
                          onChange={(e) =>
                            handleFolderChange(index, e.target.value)
                          }
                          disabled={isCreating}
                        />
                        {hasDocs && (
                          <span className="add-project-tree-count">
                            {folderDocs.length}
                          </span>
                        )}
                        <button
                          type="button"
                          className="add-project-remove-btn add-project-tree-remove"
                          onClick={() => handleRemoveFolder(index)}
                          disabled={isCreating}
                          title={t('addProjectModal.remove_folder_title')}
                          aria-label={t('addProjectModal.remove_folder_title')}
                        >
                          <DeleteIcon />
                        </button>
                      </div>
                      {hasDocs && isExpanded && (
                        <div className="add-project-tree-docs">
                          {folderDocs.map((doc) => (
                            <div
                              key={doc.documentId}
                              className="add-project-tree-doc"
                            >
                              <span className="add-project-tree-doc-icon" aria-hidden="true">
                                <DocumentIcon />
                              </span>
                              <span
                                className="add-project-tree-doc-name"
                                title={doc.originalFilename}
                              >
                                {doc.originalFilename && doc.originalFilename.length > 28
                                  ? doc.originalFilename.slice(0, 25) + '...'
                                  : doc.originalFilename || t('vcontrol.untitled_project')}
                              </span>
                              <span className="add-project-tree-doc-meta">
                                {doc.pageCount > 0 && `${doc.pageCount}p`}
                              </span>
                              <span className="add-project-tree-doc-date">
                                {doc.uploadDate
                                  ? new Date(doc.uploadDate).toLocaleDateString(undefined, {
                                      month: 'short',
                                      day: 'numeric',
                                    })
                                  : ''}
                              </span>
                              <span
                                className={`add-project-tree-doc-status ${
                                  doc.status === 'Completed' ? 'completed' : 'pending'
                                }`}
                                title={doc.status}
                                aria-label={doc.status}
                              >
                                {doc.status === 'Completed' ? <CheckIcon /> : <ClockIcon />}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* Custom Fields Section */}
          <div className="add-project-section add-project-section--fields">
            <div className="add-project-section-header">
              <label htmlFor="params-list">{t('addProjectModal.custom_fields')}</label>
              {!isLoadingProjectData && (
                <button
                  type="button"
                  className="add-project-add-btn add-project-add-btn--minimal"
                  onClick={handleAddField}
                  disabled={isCreating}
                  title={t('addProjectModal.add_field')}
                >
                  <span aria-hidden="true">+</span> {t('addProjectModal.add_field')}
                </button>
              )}
            </div>
            {isLoadingProjectData ? (
              <div className="add-project-empty add-project-empty--tree">
                {t('addProjectModal.loading_fields')}
              </div>
            ) : customFields.length === 0 ? (
              <div className="add-project-empty add-project-empty--tree">
                {t('addProjectModal.no_fields')}
              </div>
            ) : (
              <ul className="add-project-tree add-project-tree--fields">
                {customFields.map((field, index) => (
                  <li key={index}>
                    <div className="add-project-tree-node add-project-tree-node--field">
                      <span
                        className="add-project-tree-icon add-project-tree-icon--field"
                        aria-hidden="true"
                      >
                        <FieldIcon />
                      </span>
                      <input
                        type="text"
                        className="add-project-tree-input"
                        placeholder={t('addProjectModal.field_name')}
                        value={field.key}
                        onChange={(e) => handleFieldChange(index, 'key', e.target.value)}
                        disabled={isCreating}
                      />
                      <input
                        type="text"
                        className="add-project-tree-input add-project-tree-input--value"
                        placeholder={t('addProjectModal.field_value')}
                        value={field.value}
                        onChange={(e) => handleFieldChange(index, 'value', e.target.value)}
                        disabled={isCreating}
                      />
                      <button
                        type="button"
                        className="add-project-remove-btn add-project-tree-remove"
                        onClick={() => handleRemoveField(index)}
                        disabled={isCreating}
                        title={t('addProjectModal.remove_field_title')}
                        aria-label={t('addProjectModal.remove_field_title')}
                      >
                        <DeleteIcon />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

        </div>

        {/* Footer */}
        <div className="add-project-footer">
          {/* Error Message */}
          {error && (
            <div className="add-project-error">
              {error}
            </div>
          )}
          <div className="add-project-footer-actions">
            <button
              className="add-project-primary-btn"
              onClick={handleSubmit}
              disabled={isCreating || isDeleting || !newProjectName.trim() || isLoadingProjectData || (editMode && !hasChanges())}
              title={editMode && !hasChanges() ? t('addProjectModal.no_changes') : ''}
            >
              {isCreating ? (editMode ? t('addProjectModal.updating') : t('addProjectModal.creating')) : (editMode ? t('addProjectModal.update_button') : t('addProjectModal.add_button'))}
            </button>
            <button
              className="add-project-secondary-btn"
              onClick={handleClose}
              disabled={isCreating || isDeleting}
            >
              {t('addProjectModal.cancel')}
            </button>
          </div>
          {isAdmin && (
            <div className="add-project-footer-actions">
              <button
                className="add-project-secondary-btn"
                onClick={async () => {
                  const ok = await handleSaveDefaults();
                  if (ok) {
                    setError('');
                  }
                }}
                disabled={savingDefaults || isCreating}
              >
                {savingDefaults
                  ? t('addProjectModal.saving_defaults', { defaultValue: 'Saving...' })
                  : t('addProjectModal.save_as_default', { defaultValue: 'Save as Default' })}
              </button>
            </div>
          )}
          {editMode && (
            <div className="add-project-delete-area">
              {showDeleteConfirm ? (
                <div className="add-project-delete-confirm">
                  <span className="add-project-delete-warning">
                    {t('addProjectModal.confirm_delete')}
                  </span>
                  <button
                    className="add-project-danger-btn"
                    onClick={handleDelete}
                    disabled={isDeleting}
                  >
                    {isDeleting ? t('addProjectModal.deleting') : t('addProjectModal.yes_delete')}
                  </button>
                  <button
                    className="add-project-secondary-btn"
                    onClick={() => setShowDeleteConfirm(false)}
                    disabled={isDeleting}
                  >
                    {t('addProjectModal.no')}
                  </button>
                </div>
              ) : (
                <button
                  className="add-project-danger-btn"
                  onClick={handleDelete}
                  disabled={isCreating || isDeleting || isLoadingProjectData}
                  title={t('addProjectModal.delete_project')}
                >
                  {t('addProjectModal.delete_project')}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
});

AddProjectModal.displayName = 'AddProjectModal';

export default AddProjectModal;
